import { forwardRef, type SelectHTMLAttributes } from 'react'

import { cn } from '../cn'
import { inputVariants } from './Input'

// Select — a native <select> for short, fixed lists (role, vertical, plan)
// (design/components/select.html). Native on purpose: keyboard + screen
// reader for free. The chevron is decorative. For long/growing lists use a
// Combobox (a later sibling), not a 50-option Select.

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  options: { value: string; label: string }[]
  placeholder?: string
  state?: 'default' | 'error'
}

/** Native <select> on the shared field shell for short fixed lists — keyboard
 *  and screen reader for free. `placeholder` renders as a disabled empty
 *  option. For long/growing lists use Combobox, not a 50-option Select. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, state = 'default', value, defaultValue, className, ...rest },
  ref,
) {
  const isPlaceholder = (value ?? defaultValue ?? '') === ''
  // Pass only the one the caller provided, never both (avoids React's
  // controlled/uncontrolled warning).
  const controlled =
    value !== undefined ? { value } : defaultValue !== undefined ? { defaultValue } : {}
  return (
    <span className="relative block w-full">
      <select
        ref={ref}
        {...controlled}
        aria-invalid={state === 'error' || undefined}
        className={cn(
          inputVariants({ state }),
          'cursor-pointer appearance-none pr-[30px]',
          isPlaceholder && 'text-text-3',
          className,
        )}
        {...rest}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((o) => (
          <option key={o.value} value={o.value} className="text-text">
            {o.label}
          </option>
        ))}
      </select>
      <span
        aria-hidden
        className="pointer-events-none absolute right-[11px] top-1/2 -translate-y-1/2 text-text-3 [&_svg]:size-4"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </span>
    </span>
  )
})
Select.displayName = 'Select'
